import { TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { authClient } from "@/frontend/auth-client";
import { Button } from "@/frontend/components/ui/button";
import { isConnectionHealthy } from "@/frontend/utils/dashboard/connections";
import {
  usePatientAccountsQuery,
  usePatientConnectionsQuery,
} from "@/frontend/queries/dashboard/patients";
import type { ConnectionNeedingSignIn } from "@/frontend/types/dashboard/health-systems";

/**
 * The health systems this membership needs to sign in to again, each paired
 * with the FHIR base URL a fresh authorization runs against. Exported so the
 * connect surface can skip the rail column when the answer is empty.
 *
 * @param patientMembershipId the `patient_org_membership.id` to check
 */
export function useConnectionsNeedingSignIn(
  patientMembershipId: string,
): ConnectionNeedingSignIn[] {
  const { data: connectionList } = usePatientConnectionsQuery(patientMembershipId);
  const { data: accounts } = usePatientAccountsQuery(patientMembershipId);

  const fhirBaseUrlByAccountId = new Map(
    (accounts ?? []).map((account) => [account.id, account.providerId]),
  );

  return (connectionList?.connections ?? [])
    .filter((connection) => !isConnectionHealthy(connection.status))
    .map((connection) => ({
      id: connection.id,
      name: connection.sourceName,
      fhirBaseUrl: connection.accountId
        ? (fhirBaseUrlByAccountId.get(connection.accountId) ?? null)
        : null,
    }))
    .filter((row): row is ConnectionNeedingSignIn => !!row.fhirBaseUrl);
}

/**
 * The attention rail beside an individual's connect surface shows the health systems
 * that need a fresh sign-in, and nothing else.
 *
 * @param needSignIn the systems to offer a fresh sign-in for
 * @param organizationId active workspace, required by the SMART link endpoint
 */
export function IndividualConnectRail({
  needSignIn,
  organizationId,
}: {
  needSignIn: ConnectionNeedingSignIn[];
  organizationId: string | null;
}) {
  /**
   * Sends the browser back to one health system's sign-in screen. The callback
   * returns to this page, so a repaired connection lands on the list it came
   * from.
   */
  const signInAgain = async (fhirBaseUrl: string, name: string) => {
    if (!organizationId) {
      toast.error("Your workspace is still loading. Try again in a moment.");
      return;
    }
    try {
      await authClient.link.smart({
        fhirBaseUrl,
        organizationId,
        callbackURL: window.location.href,
      });
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : `Could not start the connection to ${name}`,
      );
    }
  };

  if (needSignIn.length === 0) return null;

  return (
    <div className="flex flex-col gap-4">
      {needSignIn.map((row) => (
        <div
          key={row.id}
          className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3.5"
        >
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-amber-500/15 text-amber-600 dark:text-amber-400">
            <TriangleAlert className="size-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-caption font-medium" data-patient>
              {row.name} needs a new sign-in
            </span>
            <Button
              variant="outline"
              className="mt-2.5 h-8 text-caption"
              onClick={() => void signInAgain(row.fhirBaseUrl, row.name)}
            >
              Sign in again
            </Button>
          </span>
        </div>
      ))}
    </div>
  );
}
